import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import styles from "./Popover2.module.sass";
import Icon from "../Icon";

const BODY_HEIGHT_ESTIMATE = 280;
// Reserve space above bottom bar so dropdown is never hidden behind it
const BOTTOM_BAR_RESERVE = 120;

const closeMenu = (setVisible, setInnerVisible) => {
  setVisible ? setVisible(false) : setInnerVisible(false);
};

const Popover2 = ({
  className,
  classActionsHead,
  classActionsBody,
  classActionsOption,
  items,
  actionable,
  visible,
  setVisible,
  small,
  up,
  right,
  portal,
}) => {
  const [innerVisible, setInnerVisible] = useState(false);
  const headRef = useRef(null);
  const bodyRef = useRef(null);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  // When portaling: open upward if not enough space below (avoids cutoff by viewport/filters bar)
  const [effectiveUp, setEffectiveUp] = useState(up);

  const isVisible = visible != null ? visible : innerVisible;

  const toggle = () => {
    setVisible ? setVisible(!visible) : setInnerVisible(!innerVisible);
  };

  useLayoutEffect(() => {
    if (portal && isVisible && headRef.current) {
      const rect = headRef.current.getBoundingClientRect();
      const spaceBelow = typeof window !== "undefined"
        ? window.innerHeight - rect.bottom - BOTTOM_BAR_RESERVE
        : Infinity;
      const openUp = up || spaceBelow < BODY_HEIGHT_ESTIMATE + 12;
      setEffectiveUp(openUp);
      if (openUp) {
        setPortalPosition({
          top: rect.top - BODY_HEIGHT_ESTIMATE - 12,
          left: right ? rect.right + 12 : rect.left,
        });
      } else {
        setPortalPosition({
          top: rect.bottom + 12,
          left: right ? rect.right + 12 : rect.left,
        });
      }
    }
  }, [portal, isVisible, up, right]);

  useEffect(() => {
    if (!portal || !isVisible) return;
    const handleClickOutside = (e) => {
      if (
        headRef.current?.contains(e.target) ||
        bodyRef.current?.contains(e.target)
      ) {
        return;
      }
      closeMenu(setVisible, setInnerVisible);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [portal, isVisible, setVisible]);

  const isPortaled = portal && isVisible;
  const bodyUp = portal ? effectiveUp : up;
  const bodyContent = (
    <div
      ref={portal ? bodyRef : undefined}
      className={cn(
        styles.body,
        classActionsBody,
        { [styles.portalBody]: isPortaled },
        { [styles.up]: bodyUp },
        { [styles.right]: right }
      )}
      style={portal ? { top: portalPosition.top, left: portalPosition.left } : undefined}
    >
      {items.map((x, index) => (
        <button
          type="button"
          className={cn(styles.option, classActionsOption)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            x.action();
            closeMenu(setVisible, setInnerVisible);
          }}
          key={index}
        >
          {x.icon && <Icon name={x.icon} size="24" />}
          {x.title}
        </button>
      ))}
    </div>
  );

  const content = (
    <div
      className={cn(
        styles.actions,
        className,
        {
          [styles.small]: small,
        },
        {
          [styles.up]: up,
        },
        {
          [styles.right]: right,
        },
        {
          [styles.active]: isVisible,
        },
        { [styles.portalActive]: portal && isVisible }
      )}
    >
      <div
        ref={headRef}
        className={cn(styles.head, classActionsHead)}
        onClick={() => toggle()}
        style={{ width: "auto" }}
      >
        {actionable}
      </div>
      {portal && isVisible
        ? createPortal(bodyContent, document.body)
        : bodyContent}
    </div>
  );

  if (portal) {
    return content;
  }

  return (
    <OutsideClickHandler
      onOutsideClick={() => closeMenu(setVisible, setInnerVisible)}
    >
      {content}
    </OutsideClickHandler>
  );
};

export default Popover2;
